import React, { useMemo, useState, useEffect, useRef } from "react";
import ImpersonationBanner from "./ImpersonationBanner";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Search,
  X,
  Building2,
  Activity,
  Package,
  UsersRound,
  CalendarDays,
  BedDouble,
  FileText,
  ClipboardList,
  Droplet,
  Clock3,
  Utensils,
  Receipt,
  ShieldCheck,
  Scissors,
  PackageCheck,
  Boxes,
  Warehouse,
  ShoppingCart,
  Truck,
  Barcode,
  Tags,
  RefreshCcw,
  Archive,
  CalendarCheck,
  CalendarClock,
  Banknote,
  BriefcaseBusiness,
  GraduationCap,
  Building,
  BadgeCheck,
  FileSearch,
  Bot,
  SlidersHorizontal,
  KeyRound,
  FileSpreadsheet,
  MapPin, CircleDollarSign,
  UserRound, UserPlus, Stethoscope,
  FlaskConical,
  TestTubes, Pill,
  HeartHandshake,
  Wrench,
  Timer,
  ChartNoAxesCombined,
  ListChecks,
  Landmark,
  ReceiptText,
  ScanLine,
  FileClock,
  Network,
  Globe,
  Languages, CircleHelp,
  Wallet,
  HandCoins,
  ArrowLeftRight,
  CirclePercent, NotebookTabs,
  HeartPulse,
  Hospital,
  Video,
  Radio,
  ServerCog,
  CreditCard,
  Siren
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavItems, NavItem } from "@/config/navItems";
import { useTranslation } from "react-i18next";
import { getDirection } from "@/utils/rtl";
import { useAuth } from "@/contexts/AuthContext";
import clinicalSettingsService from "@/services/clinicalSettingsService";
import EmergencyCodeBar from "@/components/shell/EmergencyCodeBar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface ErpShellProps {
  children: React.ReactNode;
  header?: React.ReactNode;
}

const submenuIcons: Record<string, NavItem["icon"]> = {
  "patients-list": UserRound, "patient-visits": CalendarDays, "patient-admissions": BedDouble,
  "medical-records": FileText, triage: ClipboardList,
  "er-dashboard": Activity, "er-visits": CalendarClock, "er-triage": HeartPulse,
  "er-triage-queue": Clock3, "er-beds": BedDouble,
  "nursing-station": ClipboardList, "nursing-dialysis": Droplet, "nursing-sessions": Timer,
  "nursing-allocation": UsersRound, "ipd-orders": FileText, "ipd-investigations": FlaskConical,
  "ipd-kitchen": Utensils, "ipd-billing": Receipt, "ipd-payer": HandCoins, "tpa-master": ShieldCheck,
  "counselor-policies": NotebookTabs, "counselor-quotes": CircleDollarSign,
  "ipd-ot-consumables": Scissors, "ipd-claims": FileSearch, "procedure-packages": Boxes,
  "package-reconciliation": RefreshCcw, "preference-card-templates": NotebookTabs,
  "staff-paging": Radio, "ipd-doctor-dashboard": Stethoscope, "doctors-list": UsersRound,
  "doctor-availability": CalendarCheck, "doctor-slots": Clock3,
  "rooms-inventory": Building, "rooms-occupancy": BedDouble, "procedure-bookings": CalendarDays,
  daycare: HeartPulse, housekeeping: Wrench, "block-bookings": CalendarClock, maintenance: Wrench,
  "room-pricing": CircleDollarSign, "room-billing": Receipt, "room-analytics": ChartNoAxesCombined,
  "appointments-heatmap": ChartNoAxesCombined, "appointments-list": CalendarDays,
  "opd-schedule-view": CalendarCheck, "opd-conflicts": CircleHelp, "opd-smart-scheduler": Bot,
  "opd-room-assignment": Building, "opd-availability": Clock3, "doctor-room-assignments": MapPin,
  "appointment-calendar": CalendarDays, "public-booking": Globe, telemedicine: Video,
  "prescriptions-list": FileText, "prescription-details": FileSearch, medications: Pill,
  "billing-worklist": ClipboardList, "unbilled-ageing": FileClock, "billing-list": ReceiptText,
  "pending-gst-submission": FileSpreadsheet, "billed-items": Tags, payments: Wallet,
  "pos-queue": Clock3, discounts: CirclePercent,
  "lab-tests-list": TestTubes, "test-types": ListChecks, "test-orders": ClipboardList,
  "test-results": FileSearch, "patient-lab-tests": UserRound, "lab-queue": Timer,
  "lab-fulfillment": PackageCheck,
  "inventory-dashboard": ChartNoAxesCombined, "inventory-items": Boxes, "inventory-stores": Warehouse,
  "inventory-stock": Package, "inventory-purchase-orders": ShoppingCart, "inventory-grn": PackageCheck,
  "inventory-issues": ArrowLeftRight, "inventory-returns": RefreshCcw, "inventory-assets": Building,
  "inventory-serials": Barcode, "inventory-consignment": HandCoins, "inventory-par-levels": SlidersHorizontal,
  "inventory-replenishment": Truck, "inventory-recalls": ShieldCheck, "inventory-adjustments": SlidersHorizontal,
  "inventory-reorder": Timer, "inventory-vendors": UsersRound, "inventory-vendor-invoices": Receipt,
  "inventory-reports": ChartNoAxesCombined,
  "medicines-list": Pill, "medicine-purchases": ShoppingCart, "medicine-sales": Wallet,
  "pharmacy-fulfillment": PackageCheck,
  "hrms-dashboard": ChartNoAxesCombined, "hrms-employees": UsersRound, "hrms-attendance": CalendarCheck,
  "hrms-shifts": Clock3, "hrms-leave": CalendarDays, "payroll-runs": FileClock,
  "payroll-salaries": Banknote, "payroll-contracts": BriefcaseBusiness, "payroll-structures": SlidersHorizontal,
  "payroll-shift-allowances": CircleDollarSign, "payroll-settings": SlidersHorizontal,
  "hrms-recruitment": UserPlus, "hrms-performance": ChartNoAxesCombined, "hrms-training": GraduationCap,
  "hrms-duty-roster": CalendarClock, "hrms-healthcare-extensions": HeartPulse, "hrms-settings": SlidersHorizontal,
  "accounting-setup": Landmark, "chart-of-accounts": ListChecks, "posting-accounts": ArrowLeftRight,
  "fiscal-years": CalendarDays, "department-cost-centers": Building, "collection-routing": Network,
  "accounting-daily": ReceiptText, "journal-entries": NotebookTabs, "manual-journals": FileText,
  "bank-reconciliation": ArrowLeftRight, "credit-notes": Receipt, "doctor-commissions": HandCoins,
  "accounting-payables": Wallet, "vendor-invoices": Receipt, "vendor-payments": Wallet,
  "vendor-credit-notes": ReceiptText, "vendor-expenses": CircleDollarSign, "ap-ageing": FileClock,
  "procurement-chain": ShoppingCart, "accounting-reports": ChartNoAxesCombined,
  "financial-statements": FileSpreadsheet, "general-ledger": Landmark, "gst-returns": FileSpreadsheet,
  "admin-staff-users": UsersRound, "admin-staff-roles": KeyRound, "go-live-checklist": ListChecks,
  "admin-departments": Building, "admin-cost-centers": Landmark, "admin-wards-units": Hospital,
  "rooms-setup": Building2, "admin-admission-masters": ClipboardList, "admin-service-catalog": Tags,
  "admin-session-types": Timer, "admin-consultation-fees": CircleDollarSign,
  "admin-hsn-reference": Barcode, "admin-item-code-config": SlidersHorizontal,
  "admin-licensing": BadgeCheck, "admin-compliance-regulations": FileText,
  "admin-compliance-documents": Archive, "admin-compliance-main": ShieldCheck,
  "quality-worklists": ListChecks, "ai-evaluation": Bot, "general-settings": SlidersHorizontal,
  "tax-setup": CirclePercent, "business-locations": MapPin, "label-templates": Tags,
  "document-numbering": FileSpreadsheet, "emergency-codes": Siren, "report-translations": Languages,
  "settings-integrations": Network, integrations: Network, "ai-settings": Bot,
  "payment-gateways": CreditCard, "public-access": Globe, "pacs-settings": ScanLine,
  "community-care": HeartHandshake, "platform-console": ServerCog,
};

function getNavItemIcon(item: NavItem): NavItem["icon"] {
  if (item.icon) return item.icon;
  if (submenuIcons[item.id]) return submenuIcons[item.id];

  const key = `${item.id} ${item.route ?? ""}`.toLowerCase();
  if (/patient|employee|staff|doctor|user/.test(key)) return UserRound;
  if (/calendar|schedule|appointment|visit/.test(key)) return CalendarDays;
  if (/bill|invoice|payment|cost|price|fee/.test(key)) return Receipt;
  if (/report|analytics|dashboard|statement/.test(key)) return ChartNoAxesCombined;
  if (/bed|room|ward|unit/.test(key)) return BedDouble;
  if (/medicine|drug|prescription|pharmacy/.test(key)) return Pill;
  if (/lab|test|investigation/.test(key)) return FlaskConical;
  if (/setting|config|template/.test(key)) return SlidersHorizontal;
  return FileText;
}

/**
 * Enterprise unified accordion sidebar navigation:
 * - Full-height sidebar running from the top edge (0px) of the screen.
 * - Collapsible from the top header with `<` / `>` toggle button.
 * - Global search at top to filter across all modules & sub-items.
 * - Single vertical list with icons + labels and inline expandable accordions.
 * - Active page highlight with rounded pill background.
 */
export default function ErpShell({ children, header }: ErpShellProps) {
  const navItems = useNavItems();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const direction = getDirection(i18n.language);
  const isRTL = direction === "rtl";

  const sidebarPreferenceKey = `hms_sidebar_open_${user?.id ?? "default"}`;
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    localStorage.getItem(sidebarPreferenceKey) !== "false"
  );
  const [hoveredModule, setHoveredModule] = useState<{ id: string; top: number } | null>(null);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navAutoClose = clinicalSettingsService.getHospitalPreferences().navAutoClose ?? true;
  useEffect(() => {
    const savedSidebarState = localStorage.getItem(sidebarPreferenceKey);
    if (savedSidebarState !== null) setSidebarOpen(savedSidebarState !== "false");
  }, [sidebarPreferenceKey]);
  useEffect(() => {
    localStorage.setItem(sidebarPreferenceKey, String(sidebarOpen));
  }, [sidebarPreferenceKey, sidebarOpen]);
  useEffect(() => () => {
    if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current);
  }, []);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedModuleIds, setExpandedModuleIds] = useState<Set<string>>(new Set());
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const navigationSearchRef = useRef<HTMLInputElement>(null);
  const focusNavigationSearchOnOpen = useRef(false);

  useEffect(() => {
    if (!sidebarOpen || !focusNavigationSearchOnOpen.current) return;
    focusNavigationSearchOnOpen.current = false;
    navigationSearchRef.current?.focus();
  }, [sidebarOpen]);

  useEffect(() => {
    const handleKeyboardShortcuts = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isEditable = !!target?.closest("input, textarea, select, [contenteditable='true'], [role='textbox']");
      const isDashboardShortcut = event.altKey && event.shiftKey && event.code === "KeyH";
      const isEmptyNavigationSearch = target === navigationSearchRef.current && !searchQuery.trim();
      if (event.key === "Escape") {
        if (shortcutsOpen) setShortcutsOpen(false);
        return;
      }
      if ((isEditable && !(isDashboardShortcut && isEmptyNavigationSearch)) || target?.closest("[role='dialog']") || event.ctrlKey || event.metaKey) return;

      if (event.key === "?") {
        event.preventDefault();
        setShortcutsOpen(true);
      } else if (event.altKey && event.shiftKey && event.code === "KeyN") {
        event.preventDefault();
        if (sidebarOpen) {
          navigationSearchRef.current?.focus();
        } else {
          focusNavigationSearchOnOpen.current = true;
          setSidebarOpen(true);
        }
      } else if (isDashboardShortcut) {
        const home = navItems.find((item) => item.id === "dashboard" || /dashboard|home/i.test(item.label));
        if (home?.route) {
          event.preventDefault();
          navigate(home.route);
        }
      }
    };
    window.addEventListener("keydown", handleKeyboardShortcuts);
    return () => window.removeEventListener("keydown", handleKeyboardShortcuts);
  }, [shortcutsOpen, sidebarOpen, searchQuery, navItems, navigate]);

  const [hospitalName, setHospitalName] = useState(() => {
    const prefsName = clinicalSettingsService.getHospitalPreferences().hospitalName;
    if (user?.hospital_name && user.hospital_name.trim() !== "Hospital" && user.hospital_name.trim() !== "") {
      return user.hospital_name;
    }
    return prefsName || "DaAFYA Medical Center";
  });

  useEffect(() => {
    const handleSettingsUpdate = (e: any) => {
      if (e.detail?.hospitalName) {
        setHospitalName(e.detail.hospitalName);
      }
    };
    window.addEventListener("hms_hospital_preferences_updated", handleSettingsUpdate);
    if (user?.hospital_name && user.hospital_name.trim() !== "Hospital" && user.hospital_name.trim() !== "") {
      setHospitalName(user.hospital_name);
    }
    return () =>
      window.removeEventListener("hms_hospital_preferences_updated", handleSettingsUpdate);
  }, [user]);

  // Collect all routes to calculate longest-match active route
  const allRoutes = useMemo(() => {
    const routes: string[] = [];
    const collect = (items: NavItem[]) => {
      items.forEach((item) => {
        if (item.route) routes.push(item.route);
        if (item.children) collect(item.children);
      });
    };
    collect(navItems);
    return routes;
  }, [navItems]);

  // Active route using longest prefix match
  const activeRoute = useMemo(() => {
    const pathname = location.pathname;
    let best: string | null = null;
    for (const route of allRoutes) {
      // A route with a query (e.g. /hrms/payroll?tab=runs) matches only when
      // the path is exact and those query values are present.
      const [routePath, routeQuery] = route.split("?");
      let matches = pathname === routePath || pathname.startsWith(routePath + "/");
      if (routeQuery) {
        const have = new URLSearchParams(location.search);
        matches = pathname === routePath && [...new URLSearchParams(routeQuery)].every(([k, v]) => have.get(k) === v);
      }
      if (matches && (!best || route.length > best.length)) {
        best = route;
      }
    }
    return best;
  }, [allRoutes, location.pathname, location.search]);

  const activeNavTrail = useMemo(() => {
    if (!activeRoute) return [] as NavItem[];
    const findTrail = (items: NavItem[], parents: NavItem[] = []): NavItem[] | null => {
      for (const item of items) {
        const trail = [...parents, item];
        if (item.route === activeRoute) return trail;
        if (item.children) {
          const nested = findTrail(item.children, trail);
          if (nested) return nested;
        }
      }
      return null;
    };
    return findTrail(navItems) ?? [];
  }, [activeRoute, navItems]);

  const isRouteActive = (route?: string) => !!route && route === activeRoute;

  // Auto-expand the parent of the currently active route
  useEffect(() => {
    if (!activeRoute) return;
    const findParents = (items: NavItem[], path: string[] = []): string[] | null => {
      for (const item of items) {
        if (item.route === activeRoute) return path;
        if (item.children) {
          const res = findParents(item.children, [...path, item.id]);
          if (res) return res;
        }
      }
      return null;
    };

    const parents = findParents(navItems);
    if (parents && parents.length > 0) {
      setExpandedModuleIds((prev) => {
        const next = new Set(prev);
        parents.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [activeRoute, navItems]);

  // Filter items based on search query
  const filteredNavItems = useMemo(() => {
    if (!searchQuery.trim()) return navItems;

    const q = searchQuery.toLowerCase();
    const filterItems = (items: NavItem[]): NavItem[] => {
      const result: NavItem[] = [];
      for (const item of items) {
        const matchesSelf = item.label.toLowerCase().includes(q);
        const matchingChildren = item.children ? filterItems(item.children) : [];
        if (matchesSelf || matchingChildren.length > 0) {
          result.push({
            ...item,
            children: matchingChildren.length > 0 ? matchingChildren : item.children,
          });
        }
      }
      return result;
    };
    return filterItems(navItems);
  }, [navItems, searchQuery]);

  // Auto-expand matching items when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      const allIds = new Set<string>();
      const collect = (items: NavItem[]) => {
        items.forEach((item) => {
          if (item.children && item.children.length > 0) {
            allIds.add(item.id);
            collect(item.children);
          }
        });
      };
      collect(filteredNavItems);
      setExpandedModuleIds(allIds);
    }
  }, [searchQuery, filteredNavItems]);

  const toggleModule = (id: string) => {
    setExpandedModuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Check if a parent module contains the active route
  const isModuleActive = (item: NavItem): boolean => {
    if (item.route && isRouteActive(item.route)) return true;
    if (item.children) {
      return item.children.some((child) => isModuleActive(child));
    }
    return false;
  };

  const cancelFlyoutClose = () => {
    if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current);
  };

  const scheduleFlyoutClose = () => {
    cancelFlyoutClose();
    hoverCloseTimer.current = setTimeout(() => setHoveredModule(null), 140);
  };

  const renderFlyoutItems = (items: NavItem[], depth = 0): React.ReactNode => (
    <div className={cn("space-y-0.5", depth > 0 && (isRTL ? "mr-3 pr-2 border-r border-border/50" : "ml-3 pl-2 border-l border-border/50"))}>
      {items.map((item) => {
        const Icon = getNavItemIcon(item);
        const active = isRouteActive(item.route) || isModuleActive(item);
        const rowClass = cn(
          "flex items-center gap-2 rounded-md px-2.5 py-2 text-xs transition-colors",
          active
            ? "bg-indigo-600/10 font-semibold text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400"
            : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
        );
        return (
          <div key={item.id}>
            {item.route ? (
              <Link
                to={item.route}
                aria-current={isRouteActive(item.route) ? "page" : undefined}
                onClick={() => {
                  setHoveredModule(null);
                  if (navAutoClose) setSidebarOpen(false);
                }}
                className={rowClass}
              >
                {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" />}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </Link>
            ) : (
              <div className={cn(rowClass, "font-medium")}>
                {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" />}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </div>
            )}
            {item.children?.length ? renderFlyoutItems(item.children, depth + 1) : null}
          </div>
        );
      })}
    </div>
  );

  const countFlyoutRows = (items: NavItem[]): number =>
    items.reduce((count, item) => count + 1 + (item.children ? countFlyoutRows(item.children) : 0), 0);

  return (
    <>
    <a href="#main-content" className="sr-only fixed left-3 top-3 z-[100] rounded-md bg-background px-4 py-2 text-sm font-medium text-foreground shadow-lg focus:not-sr-only focus:outline-none focus:ring-2 focus:ring-ring">
      Skip to main content
    </a>
    {/* Signed in as another user: shown above everything. */}
    <ImpersonationBanner />
    <div className="flex min-h-screen bg-background" dir={direction}>
      {/* Expanded Sidebar (Full Height) */}
      {sidebarOpen ? (
        <aside
          className={cn(
            "sticky top-0 h-screen flex w-64 flex-none flex-col border-border bg-card select-none transition-all duration-200 z-30",
            isRTL ? "border-l" : "border-r"
          )}
          aria-label="Sidebar Navigation"
        >
          {/* Header with Organization Title & Collapse Toggle */}
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-border/60">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400">
                <Building2 className="h-4 w-4" />
              </div>
              <span className="text-sm font-bold tracking-tight text-foreground truncate" title={hospitalName}>
                {hospitalName}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
            >
              {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          </div>

          {/* Top Search Bar */}
          <div className="px-3 pt-3 pb-2">
            <div className="relative">
              <Search
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground",
                  isRTL ? "right-2.5" : "left-2.5"
                )}
              />
              <input
                ref={navigationSearchRef}
                type="text"
                placeholder="Search navigation..."
                aria-label="Search navigation"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn(
                  "w-full rounded-md border border-border bg-background/50 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/70 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/30",
                  isRTL ? "pr-8 pl-16" : "pl-8 pr-16"
                )}
              />
              {!searchQuery && (
                <kbd
                  title="Option/Alt + Shift + N"
                  aria-label="Shortcut: Option or Alt, Shift, N"
                  className={cn(
                    "pointer-events-none absolute top-1/2 -translate-y-1/2 rounded border border-border/70 bg-muted/60 px-1.5 py-0.5 text-[10px] leading-none text-muted-foreground",
                    isRTL ? "left-2" : "right-2"
                  )}
                >
                  ⌥ ⇧ N
                </kbd>
              )}
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 rounded",
                    isRTL ? "left-2" : "right-2"
                  )}
                  title="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Navigation Items Accordion Tree */}
          <nav aria-label="Main navigation" className="flex-1 overflow-y-auto px-2 py-1.5 space-y-0.5 scrollbar-thin scrollbar-thumb-muted-foreground/20">
            {filteredNavItems.length > 0 ? (
              filteredNavItems.map((item) => {
                const Icon = item.icon;
                const hasChildren = !!(item.children && item.children.length > 0);
                const isExpanded = expandedModuleIds.has(item.id);
                const isDirectActive = isRouteActive(item.route);
                const isParentActive = isModuleActive(item);

                // Module with children -> Accordion
                if (hasChildren) {
                  return (
                    <div key={item.id} className="space-y-0.5">
                      <button
                        type="button"
                        onClick={() => toggleModule(item.id)}
                        aria-expanded={isExpanded}
                        className={cn(
                          "group flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-[13.5px] font-medium transition-colors",
                          isParentActive
                            ? "text-foreground font-semibold"
                            : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                        )}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          {Icon && (
                            <Icon
                              className={cn(
                                "h-4 w-4 flex-shrink-0 transition-colors",
                                isParentActive
                                  ? "text-indigo-600 dark:text-indigo-400"
                                  : "text-muted-foreground group-hover:text-foreground"
                              )}
                            />
                          )}
                          <span className="truncate">{item.label}</span>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        ) : isRTL ? (
                          <ChevronLeft className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        )}
                      </button>

                      {/* Expanded Sub-items */}
                      {isExpanded && (
                        <div
                          className={cn(
                            "space-y-0.5 pt-0.5",
                            isRTL ? "mr-4 pr-2 border-r border-border/40" : "ml-4 pl-2 border-l border-border/40"
                          )}
                        >
                          {item.children?.map((child) => {
                            const ChildIcon = getNavItemIcon(child);
                            const hasGrandChildren = !!(child.children && child.children.length > 0);
                            const isChildExpanded = expandedModuleIds.has(child.id);
                            const isChildActive = isRouteActive(child.route);
                            const isChildParentActive = isModuleActive(child);

                            if (hasGrandChildren) {
                              return (
                                <div key={child.id} className="space-y-0.5">
                                  <button
                                    type="button"
                                    onClick={() => toggleModule(child.id)}
                                    aria-expanded={isChildExpanded}
                                    className={cn(
                                      "flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[12.5px] transition-colors",
                                      isChildParentActive
                                        ? "text-foreground font-semibold"
                                        : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                                    )}
                                  >
                                    <span className="truncate">{child.label}</span>
                                    {isChildExpanded ? (
                                      <ChevronDown className="h-3 w-3 flex-shrink-0" />
                                    ) : (
                                      <ChevronRight className="h-3 w-3 flex-shrink-0" />
                                    )}
                                  </button>
                                  {isChildExpanded && (
                                    <div
                                      className={cn(
                                        "space-y-0.5",
                                        isRTL ? "mr-3 pr-2 border-r border-border/40" : "ml-3 pl-2 border-l border-border/40"
                                      )}
                                    >
                                      {child.children?.map((grandChild) => {
                                        const isGcActive = isRouteActive(grandChild.route);
                                        const GrandChildIcon = getNavItemIcon(grandChild);
                                        return (
                                          <Link
                                            key={grandChild.id}
                                            to={grandChild.route ?? "#"}
                                            onClick={() => { if (grandChild.route && navAutoClose) setSidebarOpen(false); }}
                                            className={cn(
                                              "flex items-center gap-2 rounded-md px-2 py-1 text-[12px] transition-colors",
                                              isGcActive
                                                ? "bg-indigo-600/10 font-semibold text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400 shadow-xs"
                                                : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                                            )}
                                          >
                                            {GrandChildIcon && <GrandChildIcon className="h-3 w-3 flex-shrink-0 opacity-75" />}
                                            <span className="truncate">{grandChild.label}</span>
                                          </Link>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            return (
                              <Link
                                key={child.id}
                                to={child.route ?? "#"}
                                onClick={() => { if (child.route && navAutoClose) setSidebarOpen(false); }}
                                className={cn(
                                  "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[12.5px] transition-colors",
                                  isChildActive
                                    ? "bg-indigo-600/10 font-semibold text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400 shadow-xs"
                                    : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                                )}
                              >
                                {ChildIcon && <ChildIcon className="h-3.5 w-3.5 flex-shrink-0 opacity-75" />}
                                <span className="truncate">{child.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                // Flat item without children (e.g. Dashboard / Home)
                return (
                  <Link
                    key={item.id}
                    to={item.route ?? "#"}
                    onClick={() => { if (item.route && navAutoClose) setSidebarOpen(false); }}
                    className={cn(
                      "group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] font-medium transition-colors",
                      isDirectActive
                        ? "bg-indigo-600/10 font-semibold text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400 shadow-xs"
                        : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                    )}
                  >
                    {Icon && (
                      <Icon
                        className={cn(
                          "h-4 w-4 flex-shrink-0 transition-colors",
                          isDirectActive
                            ? "text-indigo-600 dark:text-indigo-400"
                            : "text-muted-foreground group-hover:text-foreground"
                        )}
                      />
                    )}
                    <span className="truncate">{item.label}</span>
                  </Link>
                );
              })
            ) : (
              <div className="py-8 text-center text-xs text-muted-foreground">
                No matching navigation found
              </div>
            )}
          </nav>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-border/50 text-[11px] text-muted-foreground/70">
            <div className="flex items-center justify-between gap-2">
              <span>{t("nav.version") || "DaAFYA v2.0"}</span>
              <button type="button" onClick={() => setShortcutsOpen(true)} className="rounded px-1.5 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-keyshortcuts="?" aria-label="Keyboard shortcuts help">
                Shortcuts <kbd className="ml-1 rounded border border-border px-1">?</kbd>
              </button>
            </div>
          </div>
        </aside>
      ) : (
        /* Collapsed Sidebar Rail (Full Height) */
        <aside
          className={cn(
            "sticky top-0 h-screen flex w-14 flex-none flex-col items-center border-border bg-card py-3 select-none z-30",
            isRTL ? "border-l" : "border-r"
          )}
          aria-label="Collapsed Navigation"
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted transition-colors mb-3"
            title="Expand navigation"
          >
            {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>

          <div className="flex w-full flex-1 flex-col items-center gap-1 overflow-y-auto px-1.5 scrollbar-none">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isItemActive = isModuleActive(item);
              const targetRoute = item.route ?? (item.children?.[0]?.route ?? "#");

              return (
                <div
                  key={item.id}
                  className="flex w-full justify-center"
                  onMouseEnter={(event) => {
                    cancelFlyoutClose();
                    if (item.children?.length) {
                      const rect = event.currentTarget.getBoundingClientRect();
                      const menuHeight = Math.min(
                        window.innerHeight * 0.8,
                        countFlyoutRows(item.children ?? []) * 36 + 48
                      );
                      const top = rect.top + menuHeight <= window.innerHeight - 8
                        ? rect.top
                        : Math.max(8, rect.bottom - menuHeight);
                      setHoveredModule({ id: item.id, top });
                    } else {
                      setHoveredModule(null);
                    }
                  }}
                  onMouseLeave={scheduleFlyoutClose}
                >
                  <Link
                    to={targetRoute}
                    aria-current={isItemActive ? "page" : undefined}
                    onClick={() => {
                      setHoveredModule(null);
                      if (item.children && item.children.length > 0) {
                        setSidebarOpen(true);
                        toggleModule(item.id);
                      }
                    }}
                    title={item.label}
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
                      isItemActive
                        ? "bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400"
                        : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                    )}
                  >
                    {Icon && <Icon className="h-4 w-4" />}
                  </Link>
                </div>
              );
            })}
          </div>
          {hoveredModule && (() => {
            const item = navItems.find((candidate) => candidate.id === hoveredModule.id);
            if (!item?.children?.length) return null;
            return (
              <div
                onMouseEnter={cancelFlyoutClose}
                onMouseLeave={scheduleFlyoutClose}
                className={cn(
                  "fixed z-50 w-64 max-h-[80vh] overflow-y-auto rounded-lg border border-border bg-card p-2 shadow-xl",
                  isRTL ? "right-14" : "left-14"
                )}
                style={{ top: hoveredModule.top }}
                aria-label={`${item.label} navigation`}
              >
                <div className="mb-1.5 border-b border-border/60 px-2.5 pb-2 pt-1 text-xs font-semibold text-foreground">
                  {item.label}
                </div>
                {renderFlyoutItems(item.children)}
              </div>
            );
          })()}
        </aside>
      )}

      {/* Main Content Area with Header on top */}
      <div className="flex-1 flex flex-col min-w-0 min-h-screen">
        <EmergencyCodeBar />
        {header}
        <main id="main-content" tabIndex={-1} className="min-w-0 flex-1 overflow-x-hidden px-4 py-6 focus:outline-none">
          {activeNavTrail.length > 0 && (
            <nav aria-label="Breadcrumb" className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
              {activeNavTrail.map((item, index) => {
                const isCurrent = index === activeNavTrail.length - 1;
                return (
                  <React.Fragment key={item.id}>
                    {index > 0 && <ChevronRight aria-hidden="true" className="h-3 w-3 flex-shrink-0 opacity-60" />}
                    {isCurrent ? (
                      <span aria-current="page" className="truncate font-medium text-foreground">{item.label}</span>
                    ) : item.route ? (
                      <Link to={item.route} className="truncate hover:text-foreground">{item.label}</Link>
                    ) : (
                      <span className="truncate">{item.label}</span>
                    )}
                  </React.Fragment>
                );
              })}
            </nav>
          )}
          {children}
        </main>
      </div>
    </div>
    <Dialog open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>Use these shortcuts to move around the application.</DialogDescription>
        </DialogHeader>
        <dl className="space-y-3 text-sm">
          <div className="flex items-center justify-between gap-4"><dt>Open global search</dt><dd><kbd className="rounded border px-2 py-1">Ctrl</kbd> / <kbd className="rounded border px-2 py-1">⌘</kbd> + <kbd className="rounded border px-2 py-1">K</kbd></dd></div>
          <div className="flex items-center justify-between gap-4"><dt>Focus global search</dt><dd><kbd className="rounded border px-2 py-1">/</kbd></dd></div>
          <div className="flex items-center justify-between gap-4"><dt>Search navigation</dt><dd><kbd className="rounded border px-2 py-1">Option / Alt</kbd> + <kbd className="rounded border px-2 py-1">Shift</kbd> + <kbd className="rounded border px-2 py-1">N</kbd></dd></div>
          <div className="flex items-center justify-between gap-4"><dt>Go to dashboard</dt><dd><kbd className="rounded border px-2 py-1">Option / Alt</kbd> + <kbd className="rounded border px-2 py-1">Shift</kbd> + <kbd className="rounded border px-2 py-1">H</kbd></dd></div>
          <div className="flex items-center justify-between gap-4"><dt>Show this help</dt><dd><kbd className="rounded border px-2 py-1">?</kbd></dd></div>
          <div className="flex items-center justify-between gap-4"><dt>Close dialog or menu</dt><dd><kbd className="rounded border px-2 py-1">Esc</kbd></dd></div>
        </dl>
        <p className="text-xs text-muted-foreground">Shortcuts are paused while you type in a field.</p>
      </DialogContent>
    </Dialog>
    </>
  );
}
